import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Music } from 'lucide-react';
import MiniGameWrapper from './MiniGameWrapper';
import { MiniGameResult } from '@/types/career-sim';

interface Note {
  id: string;
  lane: number;
  position: number;
}

interface RecordingGameProps {
  onComplete: (result: MiniGameResult) => void;
  onExit: () => void;
}

const RecordingGame: React.FC<RecordingGameProps> = ({ onComplete, onExit }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [score, setScore] = useState(0);
  const [hits, setHits] = useState(0);
  const [totalNotes, setTotalNotes] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15);
  const [gameActive, setGameActive] = useState(true);

  const lanes = [0, 1, 2, 3];
  const keys = ['a', 's', 'd', 'f'];

  // Generate notes
  useEffect(() => {
    if (!gameActive) return;

    const interval = setInterval(() => {
      const newNote: Note = {
        id: Math.random().toString(36).substr(2, 9),
        lane: Math.floor(Math.random() * 4),
        position: 0,
      };
      setNotes(prev => [...prev, newNote]);
      setTotalNotes(prev => prev + 1);
    }, 800);

    return () => clearInterval(interval);
  }, [gameActive]);

  // Move notes down
  useEffect(() => {
    if (!gameActive) return;

    const interval = setInterval(() => {
      setNotes(prev => {
        return prev
          .map(note => ({ ...note, position: note.position + 5 }))
          .filter(note => note.position < 100);
      });
    }, 50);

    return () => clearInterval(interval);
  }, [gameActive]);

  // Timer
  useEffect(() => {
    if (timeLeft <= 0) {
      endGame();
      return;
    }

    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  const handleKeyPress = useCallback((lane: number) => {
    setNotes(prev => {
      const noteInLane = prev.find(n => n.lane === lane && n.position >= 80 && n.position <= 95);
      if (noteInLane) {
        const accuracy = 95 - Math.abs(90 - noteInLane.position);
        const points = Math.floor(accuracy * 10);
        setScore(s => s + points);
        setHits(h => h + 1);
        return prev.filter(n => n.id !== noteInLane.id);
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const laneIndex = keys.indexOf(e.key.toLowerCase());
      if (laneIndex !== -1) {
        handleKeyPress(laneIndex);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress]);

  const endGame = () => {
    setGameActive(false);
    const accuracy = totalNotes > 0 ? (hits / totalNotes) * 100 : 0;
    const multiplier = accuracy >= 90 ? 1.5 : accuracy >= 70 ? 1.2 : accuracy >= 50 ? 1.0 : 0.75;
    
    setTimeout(() => {
      onComplete({
        score: accuracy,
        multiplier,
        success: accuracy >= 50,
      });
    }, 500);
  };

  return (
    <MiniGameWrapper
      title="🎵 Recording Session"
      instructions="Hit the keys (A, S, D, F) when notes reach the target zone!"
      timeLeft={timeLeft}
      maxTime={15}
      onExit={onExit}
    >
      <div className="flex flex-col h-full">
        {/* Score Display */}
        <div className="flex justify-between items-center mb-4 text-white">
          <div className="text-xl font-bold">Score: {score}</div>
          <div className="text-sm">
            Hits: {hits}/{totalNotes} ({totalNotes > 0 ? Math.round((hits / totalNotes) * 100) : 0}%)
          </div>
        </div>

        {/* Game Area */}
        <div className="flex-1 relative bg-black/50 rounded-lg overflow-hidden">
          <div className="flex h-full">
            {lanes.map((lane, index) => (
              <div key={lane} className="flex-1 relative border-r border-purple-500/30 last:border-r-0">
                {/* Target Zone */}
                <div className="absolute bottom-12 left-0 right-0 h-12 bg-purple-500/20 border-y-2 border-purple-400 flex items-center justify-center">
                  <span className="text-white font-bold">{keys[index].toUpperCase()}</span>
                </div>

                {/* Notes */}
                {notes
                  .filter(note => note.lane === lane)
                  .map(note => (
                    <motion.div
                      key={note.id}
                      className="absolute left-1/2 -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg"
                      style={{ top: `${note.position}%` }}
                    >
                      <Music className="h-6 w-6 text-white" />
                    </motion.div>
                  ))}
              </div>
            ))}
          </div>
        </div>

        {/* Key Hints */}
        <div className="flex gap-2 mt-4 justify-center">
          {keys.map(key => (
            <button
              key={key}
              onClick={() => handleKeyPress(keys.indexOf(key))}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-colors"
            >
              {key.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </MiniGameWrapper>
  );
};

export default RecordingGame;
